import React, { useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import savannahVideo from '../assets/savannah.MP4';
import yangoImage from '../assets/yango_image.png';
import wheelbarrowImage from '../assets/generated/wheelbarrow-workshop-v2.png';
import waterImage from '../assets/generated/water-production-v2.png';

const WhyInvestCameroonSection = () => {
  const { t, i18n } = useTranslation();
  const copy = i18n.language?.startsWith('fr') ? {
    intro: "De la mobilité urbaine à la production locale et aux services financiers, nos investissements soutiennent des activités concrètes qui créent de la valeur au Cameroun.",
    achievements: 'Réalisations', status: 'En cours de développement', ongoingTitle: "Unité de production d’eau",
    ongoingDescription: "APED développe une unité moderne axée sur un traitement fiable, un embouteillage efficace et une distribution locale responsable.",
    ongoingAlt: "Ingénieurs examinant une nouvelle ligne de production et d’embouteillage d’eau",
    items: {
      vtc: { title: 'La mobilité urbaine à grande échelle', description: "Une équipe dédiée gère une flotte de plus de 50 véhicules avec chauffeur via notre partenaire stratégique de mobilité, avec un service disponible 24h/24.", alt: "Chauffeur auprès d’un véhicule de notre partenaire de mobilité au Cameroun" },
      financial: { title: 'Agence Savana, Logpom', description: "Ouverte en juin 2025, cette agence de 60 m² rapproche des clients de Douala les services d’épargne, de mobile money, de microfinance islamique, de cash management et de transfert international.", alt: "Visite vidéo de l’agence de services financiers Savana à Logpom" },
      manufacturing: { title: 'Une production locale et circulaire', description: "Notre programme de brouettes multi-usages transforme le métal recyclé en équipements durables pour le BTP et l’agriculture, tout en renforçant les capacités de production locales.", alt: "Techniciens contrôlant une brouette verte fabriquée localement" },
    }
  } : {
    intro: 'From urban mobility to local manufacturing and financial services, our investments are built around operating businesses with measurable local value.',
    achievements: 'Achievements', status: 'Now in development', ongoingTitle: 'Water production unit',
    ongoingDescription: 'APED is developing a modern facility designed around reliable processing, efficient bottling and responsible local distribution.',
    ongoingAlt: 'Engineers reviewing a new water production and bottling line',
    items: {
      vtc: { title: 'Urban mobility at scale', description: 'A dedicated operations team manages a fleet of more than 50 chauffeur-driven vehicles through our strategic mobility partner, with service available around the clock.', alt: 'Driver beside an APED partner mobility vehicle in Cameroon' },
      financial: { title: 'Savana branch, Logpom', description: 'Opened in June 2025, the 60 m² branch brings savings, mobile money, Islamic microfinance, cash management and international transfers closer to customers in Douala.', alt: 'Video tour of the Savana financial-services branch in Logpom' },
      manufacturing: { title: 'Local, circular manufacturing', description: 'Our multi-purpose wheelbarrow program turns recycled metal into durable equipment for construction and agriculture while strengthening local production capacity.', alt: 'Technicians inspecting a locally manufactured green steel wheelbarrow' },
    }
  };
  const bankVideo = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const toggleVideo = async () => {
    if (!bankVideo.current) return;
    if (bankVideo.current.paused) {
      try { await bankVideo.current.play(); setIsPlaying(true); } catch { setIsPlaying(false); }
    } else {
      bankVideo.current.pause();
      setIsPlaying(false);
    }
  };

  const achievements = [
    { key: 'vtc', image: yangoImage, ...copy.items.vtc },
    { key: 'financial', video: savannahVideo, ...copy.items.financial },
    { key: 'manufacturing', image: wheelbarrowImage, ...copy.items.manufacturing },
  ];

  return (
    <section className="projects" aria-labelledby="projects-title">
      <div className="projects__inner">
        <header className="projects__header">
          <h2 id="projects-title">{t('projects.title')}</h2>
          <p>{copy.intro}</p>
        </header>

        <div className="project-group">
          <h3>{copy.achievements}</h3>
          <div className="achievement-list">
            {achievements.map((item, index) => (
              <article className="achievement" key={item.key}>
                <div className="achievement__media">
                  {item.video ? (
                    <>
                      <video ref={bankVideo} src={item.video} preload="metadata" playsInline onEnded={() => setIsPlaying(false)} aria-label={item.alt} />
                      <button className="video-control" onClick={toggleVideo} aria-label={isPlaying ? t('gallery.pause') : t('gallery.play')}>
                        {isPlaying ? <Pause /> : <Play />}
                      </button>
                    </>
                  ) : (
                    <img src={item.image} alt={item.alt} loading={index === 0 ? 'eager' : 'lazy'} />
                  )}
                </div>
                <div className="achievement__copy">
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>

        <article className="ongoing">
          <div className="ongoing__media"><img src={waterImage} alt={copy.ongoingAlt} loading="lazy" /></div>
          <div className="ongoing__copy">
            <p className="status">{copy.status}</p>
            <h3>{copy.ongoingTitle}</h3>
            <p>{copy.ongoingDescription}</p>
          </div>
        </article>
      </div>

      <style jsx>{`
        .projects { padding: clamp(5rem, 9vw, 8rem) 1.25rem; background: #eef1f4; color: #142238; }
        .projects__inner { width: min(1200px, 100%); margin: 0 auto; }
        .projects__header { display: grid; grid-template-columns: 1.1fr .9fr; align-items: end; gap: 3rem; margin-bottom: clamp(3rem, 6vw, 5.5rem); }
        .projects__header h2 { margin: 0; max-width: 11ch; font-size: clamp(2.6rem, 6vw, 5rem); line-height: 1; letter-spacing: -.035em; }
        .projects__header p { margin: 0; max-width: 48ch; font-size: 1.1rem; line-height: 1.65; color: #435066; }
        .project-group > h3 { margin: 0 0 1.5rem; font-size: 1.2rem; }
        .achievement-list { border-top: 1px solid #bcc4ce; }
        .achievement { display: grid; grid-template-columns: minmax(280px, .9fr) 1.1fr; gap: clamp(1.5rem, 5vw, 5rem); padding: clamp(1.5rem, 3vw, 2.5rem) 0; border-bottom: 1px solid #bcc4ce; align-items: center; }
        .achievement:nth-child(even) .achievement__media { order: 2; }
        .achievement__media, .ongoing__media { position: relative; aspect-ratio: 16/10; overflow: hidden; background: #d9dee5; }
        .achievement__media img, .achievement__media video, .ongoing__media img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .achievement__copy { display: grid; grid-template-columns: 2.5rem 1fr; column-gap: 1rem; align-items: start; }
        .achievement__copy span { color: #85763e; font-weight: 700; }
        .achievement__copy h4 { margin: 0 0 .75rem; font-size: clamp(1.5rem, 3vw, 2.25rem); line-height: 1.1; }
        .achievement__copy p { grid-column: 2; margin: 0; max-width: 54ch; line-height: 1.65; color: #435066; }
        .video-control { position: absolute; inset: auto auto 1rem 1rem; width: 3rem; height: 3rem; display: grid; place-items: center; border: 0; border-radius: 50%; background: #fff; color: #142238; cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,.18); }
        .video-control svg { width: 1.2rem; height: 1.2rem; }
        .ongoing { display: grid; grid-template-columns: 1.35fr .65fr; margin-top: clamp(4rem, 8vw, 7rem); background: #132238; color: white; }
        .ongoing__media { aspect-ratio: 16/10; }
        .ongoing__copy { display: flex; flex-direction: column; justify-content: center; padding: clamp(2rem, 5vw, 4.5rem); }
        .ongoing__copy .status { margin: 0 0 1rem; color: #d4bd64; font-weight: 700; }
        .ongoing__copy h3 { margin: 0 0 1rem; font-size: clamp(2rem, 4vw, 3.4rem); line-height: 1; }
        .ongoing__copy p:last-child { margin: 0; line-height: 1.65; color: #dbe1e8; }
        @media (max-width: 760px) {
          .projects__header, .achievement, .ongoing { grid-template-columns: 1fr; }
          .projects__header { gap: 1.25rem; }
          .achievement:nth-child(even) .achievement__media { order: 0; }
          .achievement__copy { padding: .5rem 0 1rem; }
          .ongoing__copy { padding: 2rem 1.5rem 2.5rem; }
        }
      `}</style>
    </section>
  );
};

export default WhyInvestCameroonSection;
